// 絵文字データ（動的解析）
import { useState, useEffect } from 'react';

// 型定義
export type Emoji = {
  id: string;
  emoji: string;
  name: string; // 英語名
  japaneseName: string; // 日本語名
};

export type EmojiCategory = {
  name: string;
  emojis: Array<Emoji>;
};

// コードポイントを絵文字文字に変換
function codePointsToEmoji(codePoints: string[]): string {
  return codePoints
    .map(cp => String.fromCodePoint(parseInt(cp, 16)))
    .join('');
}

// IDを生成（絵文字名から）
function generateId(name: string, index: number): string {
  // 英数字とハイフン、アンダースコアのみを使用
  const base = name
    .toLowerCase()
    .replace(/[^a-z0-9\s-]/g, '')
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '');
  
  return `emoji-${base || `item-${index}`}`;
}

// グループ名を日本語に変換
const groupNameMap: Record<string, string> = {
  'Smileys & Emotion': '笑顔・感情',
  'People & Body': '人・体',
  'Animals & Nature': '動物・自然',
  'Food & Drink': '食べ物・飲み物',
  'Travel & Places': '旅行・場所',
  'Activities': '活動',
  'Objects': 'オブジェクト',
  'Symbols': '記号',
  'Flags': '旗',
  'Component': 'コンポーネント',
};

function translateGroupName(englishName: string): string {
  return groupNameMap[englishName] || englishName;
}

// 日本語→英語の逆引きマッピングを生成（検索用）
export function createJapaneseToEnglishMap(): Record<string, string> {
  const wordMap: Record<string, string> = {
    'face': '顔',
    'hand': '手',
    'person': '人',
    'people': '人々',
    'man': '男性',
    'woman': '女性',
    'child': '子供',
    'baby': '赤ちゃん',
    'smiling': '笑顔',
    'grinning': 'にっこり',
    'winking': 'ウインク',
    'kissing': 'キス',
    'heart': 'ハート',
    'eyes': '目',
    'tears': '涙',
    'sweat': '汗',
    'food': '食べ物',
    'drink': '飲み物',
    'animal': '動物',
    'dog': '犬',
    'cat': '猫',
    'bird': '鳥',
    'fish': '魚',
    'tree': '木',
    'flower': '花',
    'sun': '太陽',
    'moon': '月',
    'star': '星',
    'car': '車',
    'train': '電車',
    'plane': '飛行機',
    'house': '家',
    'building': '建物',
    'flag': '旗',
    'button': 'ボタン',
    'symbol': '記号',
  };

  const directMap: Record<string, string> = {
    'grinning face': 'にっこり笑う顔',
    'grinning face with big eyes': '大きな目でにっこり笑う顔',
    'grinning face with smiling eyes': '笑みを浮かべた目でにっこり笑う顔',
    'beaming face with smiling eyes': '笑みを浮かべた目で輝く顔',
    'grinning squinting face': '目を細めてにっこり笑う顔',
    'grinning face with sweat': '汗をかいてにっこり笑う顔',
    'rolling on the floor laughing': '床に転がって大笑い',
    'face with tears of joy': '喜びの涙を流す顔',
    'slightly smiling face': 'ほほえむ顔',
    'upside-down face': '逆さまの顔',
    'melting face': '溶ける顔',
    'winking face': 'ウインクする顔',
    'smiling face with smiling eyes': '笑みを浮かべた目で笑う顔',
    'smiling face with halo': '後光が差す笑顔',
    'smiling face with hearts': 'ハートで笑う顔',
    'smiling face with heart-eyes': 'ハートの目で笑う顔',
    'star-struck': '星に打たれた',
    'face blowing a kiss': 'キスを飛ばす顔',
    'kissing face': 'キスする顔',
    'kissing face with closed eyes': '目を閉じてキスする顔',
    'kissing face with smiling eyes': '笑みを浮かべた目でキスする顔',
    'smiling face with tear': '涙を流す笑顔',
    'thumbs up': 'いいね',
    'thumbs down': 'よくないね',
    'clapping hands': '拍手',
    'handshake': '握手',
  };

  // 逆引きマッピングを生成
  const reverseMap: Record<string, string> = {};
  
  // wordMapから逆引き
  for (const [english, japanese] of Object.entries(wordMap)) {
    reverseMap[japanese] = english;
  }
  
  // directMapから逆引き
  for (const [english, japanese] of Object.entries(directMap)) {
    reverseMap[japanese] = english;
  }
  
  // よく使われる日本語キーワードの追加マッピング
  const additionalMap: Record<string, string> = {
    '笑う': 'smiling',
    '泣く': 'crying',
    '怒る': 'angry',
    '悲しい': 'sad',
    '喜び': 'joy',
    '驚き': 'surprised',
    '恐れる': 'fearful',
    '疲れる': 'tired',
    '眠る': 'sleeping',
    '考える': 'thinking',
    '困惑': 'confused',
    '心配': 'worried',
    '不安': 'anxious',
    '失望': 'disappointed',
  };
  
  return { ...reverseMap, ...additionalMap };
}

// 絵文字名を日本語に変換（簡易マッピング）
function translateEmojiName(englishName: string): string {
  // 基本的な単語の翻訳マッピング
  const wordMap: Record<string, string> = {
    'face': '顔',
    'hand': '手',
    'person': '人',
    'people': '人々',
    'man': '男性',
    'woman': '女性',
    'child': '子供',
    'baby': '赤ちゃん',
    'smiling': '笑顔',
    'grinning': 'にっこり',
    'winking': 'ウインク',
    'kissing': 'キス',
    'heart': 'ハート',
    'eyes': '目',
    'tears': '涙',
    'sweat': '汗',
    'food': '食べ物',
    'drink': '飲み物',
    'animal': '動物',
    'dog': '犬',
    'cat': '猫',
    'bird': '鳥',
    'fish': '魚',
    'tree': '木',
    'flower': '花',
    'sun': '太陽',
    'moon': '月',
    'star': '星',
    'car': '車',
    'train': '電車',
    'plane': '飛行機',
    'house': '家',
    'building': '建物',
    'flag': '旗',
    'button': 'ボタン',
    'symbol': '記号',
  };

  // よく使われる絵文字名の直接マッピング
  const directMap: Record<string, string> = {
    'grinning face': 'にっこり笑う顔',
    'grinning face with big eyes': '大きな目でにっこり笑う顔',
    'grinning face with smiling eyes': '笑みを浮かべた目でにっこり笑う顔',
    'beaming face with smiling eyes': '笑みを浮かべた目で輝く顔',
    'grinning squinting face': '目を細めてにっこり笑う顔',
    'grinning face with sweat': '汗をかいてにっこり笑う顔',
    'rolling on the floor laughing': '床に転がって大笑い',
    'face with tears of joy': '喜びの涙を流す顔',
    'slightly smiling face': 'ほほえむ顔',
    'upside-down face': '逆さまの顔',
    'melting face': '溶ける顔',
    'winking face': 'ウインクする顔',
    'smiling face with smiling eyes': '笑みを浮かべた目で笑う顔',
    'smiling face with halo': '後光が差す笑顔',
    'smiling face with hearts': 'ハートで笑う顔',
    'smiling face with heart-eyes': 'ハートの目で笑う顔',
    'star-struck': '星に打たれた',
    'face blowing a kiss': 'キスを飛ばす顔',
    'kissing face': 'キスする顔',
    'kissing face with closed eyes': '目を閉じてキスする顔',
    'kissing face with smiling eyes': '笑みを浮かべた目でキスする顔',
    'smiling face with tear': '涙を流す笑顔',
    'face savoring food': '食べ物を味わう顔',
    'face with tongue': '舌を出す顔',
    'winking face with tongue': '舌を出してウインクする顔',
    'zany face': 'おかしな顔',
    'squinting face with tongue': '舌を出して目を細める顔',
    'money-mouth face': 'お金の口の顔',
    'smiling face with open hands': '開いた手で笑う顔',
    'face with hand over mouth': '口に手を当てた顔',
    'face with open eyes and hand over mouth': '大きく開いた目と口に手を当てた顔',
    'face with peeking eye': 'のぞき見する顔',
    'shushing face': '静かにする顔',
    'thinking face': '考える顔',
    'saluting face': '敬礼する顔',
    'zipper-mouth face': '口をチャックする顔',
    'face with raised eyebrow': '眉毛を上げた顔',
    'neutral face': '無表情な顔',
    'expressionless face': '表情のない顔',
    'face without mouth': '口のない顔',
    'dotted line face': '点線の顔',
    'face in clouds': '雲の中の顔',
    'smirking face': 'ニヤリとする顔',
    'unamused face': '面白くない顔',
    'face with rolling eyes': '白目をむく顔',
    'grimacing face': 'しかめ面',
    'face exhaling': '息を吐く顔',
    'lying face': '嘘をつく顔',
    'shaking face': '揺れる顔',
    'head shaking horizontally': '頭を横に振る',
    'head shaking vertically': '頭を縦に振る',
    'relieved face': '安堵した顔',
    'pensive face': '物思いにふける顔',
    'sleepy face': '眠そうな顔',
    'drooling face': 'よだれを垂らす顔',
    'sleeping face': '眠る顔',
    'face with bags under eyes': '目の下にクマがある顔',
    'face with medical mask': '医療用マスクをした顔',
    'face with thermometer': '体温計を持つ顔',
    'face with head-bandage': '頭に包帯をした顔',
    'nauseated face': '吐き気を催す顔',
    'face vomiting': '嘔吐する顔',
    'sneezing face': 'くしゃみをする顔',
    'hot face': '熱い顔',
    'cold face': '冷たい顔',
    'woozy face': 'めまいがする顔',
    'face with crossed-out eyes': '目が×印の顔',
    'face with spiral eyes': '渦巻きの目の顔',
    'exploding head': '爆発する頭',
    'cowboy hat face': 'カウボーイハットの顔',
    'partying face': 'パーティーの顔',
    'disguised face': '変装した顔',
    'smiling face with sunglasses': 'サングラスをかけた笑顔',
    'nerd face': 'オタクの顔',
    'face with monocle': 'モノクルをかけた顔',
    'confused face': '混乱した顔',
    'face with diagonal mouth': '斜めの口の顔',
    'worried face': '心配した顔',
    'slightly frowning face': '少ししかめ面',
    'frowning face': 'しかめ面',
    'face with open mouth': '口を開けた顔',
    'hushed face': '静かな顔',
    'astonished face': '驚いた顔',
    'flushed face': '赤面した顔',
    'distorted face': '歪んだ顔',
    'pleading face': '懇願する顔',
    'face holding back tears': '涙をこらえる顔',
    'frowning face with open mouth': '口を開けてしかめ面',
    'anguished face': '苦悩した顔',
    'fearful face': '恐れた顔',
    'anxious face with sweat': '汗をかいて不安な顔',
    'sad but relieved face': '悲しいが安堵した顔',
    'crying face': '泣く顔',
    'loudly crying face': '大声で泣く顔',
    'face screaming in fear': '恐怖で叫ぶ顔',
    'confounded face': '困惑した顔',
    'persevering face': '忍耐強い顔',
    'disappointed face': '失望した顔',
    'downcast face with sweat': '汗をかいてうつむく顔',
    'weary face': '疲れた顔',
    'tired face': '疲労した顔',
    'yawning face': 'あくびをする顔',
    'face with steam from nose': '鼻から湯気が出る顔',
    'enraged face': '激怒した顔',
    'angry face': '怒った顔',
    'face with symbols on mouth': '口に記号がある顔',
    'smiling face with horns': '角のある笑顔',
    'angry face with horns': '角のある怒った顔',
    'skull': '頭蓋骨',
    'skull and crossbones': 'ドクロと骨',
    'pile of poo': 'うんち',
    'clown face': 'ピエロの顔',
    'ogre': '鬼',
    'goblin': 'ゴブリン',
    'ghost': 'お化け',
    'alien': 'エイリアン',
    'alien monster': 'エイリアンモンスター',
    'robot': 'ロボット',
    'thumbs up': 'いいね',
    'thumbs down': 'よくないね',
    'raised fist': '上げた拳',
    'oncoming fist': '向かってくる拳',
    'left-facing fist': '左向きの拳',
    'right-facing fist': '右向きの拳',
    'clapping hands': '拍手',
    'raising hands': '手を上げる',
    'heart hands': 'ハートの手',
    'open hands': '開いた手',
    'palms up together': '手のひらを上に向けて合わせる',
    'handshake': '握手',
    'folded hands': '手を合わせる',
    'index pointing up': '上を指す人差し指',
    'middle finger': '中指',
    'backhand index pointing down': '下を指す手の甲の人差し指',
    'index pointing left': '左を指す人差し指',
    'index pointing right': '右を指す人差し指',
  };

  // 直接マッピングがある場合はそれを使用
  const lowerName = englishName.toLowerCase();
  if (directMap[lowerName]) {
    return directMap[lowerName];
  }

  // パターンベースの翻訳
  const words = lowerName.split(/\s+/);
  const translatedWords = words.map(word => {
    const cleanWord = word.replace(/[.,!?;:()]/g, '');
    if (wordMap[cleanWord]) {
      return wordMap[cleanWord];
    }
    if (cleanWord.endsWith('s') && wordMap[cleanWord.slice(0, -1)]) {
      return wordMap[cleanWord.slice(0, -1)];
    }
    return word;
  });

  const result = translatedWords.join(' ');
  const hasTranslation = translatedWords.some((word, index) => word !== words[index]);
  
  return hasTranslation ? result : englishName;
}

// メイン解析関数
async function parseEmojiData(data: string, excludeList: Set<string>): Promise<EmojiCategory[]> {
  const lines = data.split('\n');

  const categories: EmojiCategory[] = [];
  let currentGroup: string | null = null;
  let currentCategory: EmojiCategory | null = null;
  let emojiIndex = 0;
  const seen = new Set<string>();
  for (const line of lines) {
    const trimmed = line.trim();

    // 空行やコメント行をスキップ
    if (!trimmed || (trimmed.startsWith('#') && !trimmed.includes('group:') && !trimmed.includes('subgroup:'))) {
      continue;
    }

    // グループ行を解析
    const groupMatch = trimmed.match(/^# group:\s*(.+)$/);
    if (groupMatch) {
      const groupName = groupMatch[1].trim();
      currentGroup = translateGroupName(groupName);
      
      // 新しいカテゴリを作成
      currentCategory = {
        name: currentGroup,
        emojis: [],
      };
      categories.push(currentCategory);
      continue;
    }

    // サブグループ行は無視（グループ単位でまとめる）
    if (trimmed.startsWith('# subgroup:')) {
      continue;
    }

    // 絵文字行を解析
    // フォーマット: code points; status # emoji name
    // const emojiMatch = trimmed.match(/^([0-9A-F\s]+)\s*;\s*(fully-qualified|minimally-qualified|unqualified|component)\s*#\s*(.+?)\s+(.+)$/);
    const emojiMatch = trimmed.match(
      /^([0-9A-F\s]+)\s*;\s*(fully-qualified|minimally-qualified|unqualified|component)\s*#\s*(\S+)\s+E([\d.]+)\s+(.+)$/
    );
    
    if (emojiMatch) {
      if (!currentCategory) {
        currentCategory = { name: '未分類', emojis: [] };
        categories.push(currentCategory);
      }

      const [, codePointsStr, status, , , emojiName] = emojiMatch;

      // const codePointsStr = emojiMatch[1].trim();
      // const status = emojiMatch[2];
      // const emojiName = emojiMatch[4].trim();

      // fully-qualifiedのみを対象とする（重複を避けるため）
      if (status !== 'fully-qualified') {
        continue;
      }

      // コードポイントを解析
      const codePoints = codePointsStr.split(/\s+/).filter(cp => cp.length > 0);
      
      // 絵文字文字を生成
      const emoji = codePointsToEmoji(codePoints);

      // スキントーンやバリエーションを含むものはスキップ（基本形のみ）
      // 1F3FB-1F3FFはスキントーン、200Dはゼロ幅結合子
      if (codePoints.some(cp => ['1F3FB', '1F3FC', '1F3FD', '1F3FE', '1F3FF'].includes(cp))) {
        continue;
      }

      // 除外リストに含まれている場合はスキップ（絵文字名でチェック）
      if (excludeList.has(emojiName)) {
        continue;
      }

      // IDを生成
      const id = generateId(emojiName, emojiIndex++);

      // ② 重複チェック
      if (!seen.has(emoji)) {
        currentCategory.emojis.push({
          id,
          emoji,
          name: emojiName, // 英語名
          japaneseName: translateEmojiName(emojiName), // 日本語名
        });
        seen.add(emoji);
      }
    }
  }

  return categories;
}

// 絵文字データを読み込むフック
let cachedData: EmojiCategory[] | null = null;
let loadingPromise: Promise<EmojiCategory[]> | null = null;

async function loadEmojiData(): Promise<EmojiCategory[]> {
  if (cachedData) {
    return cachedData;
  }

  if (loadingPromise) {
    return loadingPromise;
  }

  loadingPromise = Promise.all([
    fetch('/emoji-data.txt').then(response => {
      if (!response.ok) {
        throw new Error('Failed to load emoji data');
      }
      return response.text();
    }),
    loadExcludeList()
  ])
    .then(([data, excludeList]) => {
      return parseEmojiData(data, excludeList);
    })
    .then(parsed => {
      cachedData = parsed;
      return parsed;
    })
    .catch(error => {
      console.error('Error loading emoji data:', error);
      loadingPromise = null;
      return [];
    });

  return loadingPromise;
}

// 実行時に解析（初期値は空配列、後で読み込まれる）
export let emojiCategories: EmojiCategory[] = [];

// データを読み込む関数（Top.tsxなどで使用）
export function useEmojiCategories(): EmojiCategory[] {
  const [categories, setCategories] = useState<EmojiCategory[]>([]);

  useEffect(() => {
    let active = true;
    loadEmojiData().then(data => {
      if (active) {
        emojiCategories = data;
        setCategories(data);
      }
    });
    return () => { active = false; };
  }, []);

  return categories;
}



// 初期化（非同期）
loadEmojiData().then(data => {
  emojiCategories = data;
  // 解析結果をログに出力
  const totalEmojis = data.reduce((sum, cat) => sum + cat.emojis.length, 0);
  console.log('📊 絵文字データ解析完了:', {
    カテゴリ数: data.length,
    総絵文字数: totalEmojis,
    カテゴリ別: data.map(cat => ({
      カテゴリ: cat.name,
      絵文字数: cat.emojis.length
    }))
  });
});

// 除外リストを読み込む
let cachedExcludeList: Set<string> | null = null;
let loadingExcludeListPromise: Promise<Set<string>> | null = null;

async function loadExcludeList(): Promise<Set<string>> {
  if (cachedExcludeList) {
    return cachedExcludeList;
  }

  if (loadingExcludeListPromise) {
    return loadingExcludeListPromise;
  }

  loadingExcludeListPromise = fetch('/emoji-exclude-list.json')
    .then(response => {
      if (!response.ok) {
        // ファイルが存在しない場合は空のセットを返す
        return new Set<string>();
      }
      return response.json();
    })
    .then((data: string[]) => {
      // 除外リストは絵文字名の配列
      const excludeSet = new Set(data);
      cachedExcludeList = excludeSet;
      return excludeSet;
    })
    .catch(error => {
      console.warn('除外リストの読み込みに失敗しました（初回実行時は正常です）:', error);
      cachedExcludeList = new Set<string>();
      return cachedExcludeList;
    });

  return loadingExcludeListPromise;
}
